<template>
    <div class="legacy-rfp-details p-grid">
        <div class="p-col-12">
            <div class="card">
                <div v-if="loading" class="loading-state">
                    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                    <span>Loading legacy RFP...</span>
                </div>

                <template v-else-if="rfpDetails">
                    <div class="page-header">
                        <div class="title-block">
                            <h2>{{ rfpDetails.name }}</h2>
                        </div>
                    </div>

                    <div v-if="apiError" class="api-error-banner">
                        <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                        <span>{{ apiError }}</span>
                    </div>

                    <div class="card-section">
                        <TabView v-model:activeIndex="activeTabIndex">
                            <TabPanel header="Common Details">
                                <div class="tab-content overview-tab">
                                    <div class="overview-grid">
                                        <div class="overview-main">
                                            <section v-if="rfpDetails.description" class="overview-section">
                                                <h4 class="section-title">Summary</h4>
                                                <div class="rfp-description"
                                                     v-html="formatRichText(rfpDetails.description)"></div>
                                            </section>

                                            <section v-if="rfpDetails.attachments?.length" class="overview-section">
                                                <h4>Attachments</h4>
                                                <ul class="rfp-doc-list">
                                                    <li v-for="doc in rfpDetails.attachments"
                                                        :key="doc.id || doc.name">
                                                        <a :href="doc.url || buildDocumentLink(doc)"
                                                           target="_blank"
                                                           rel="noopener">
                                                            {{ doc.name }}
                                                        </a>
                                                    </li>
                                                </ul>
                                            </section>

                                            <section v-if="rfpDetails.links?.length" class="overview-section">
                                                <h4>Links</h4>
                                                <ul class="rfp-link-list">
                                                    <li v-for="link in rfpDetails.links" :key="link.url">
                                                        <a :href="buildExternalUrl(link.url)" target="_blank"
                                                           rel="noopener">
                                                            {{ link.label }}
                                                        </a>
                                                    </li>
                                                </ul>
                                            </section>

                                            <section v-if="rfpDetails.commonDetails?.length"
                                                     class="overview-section">
                                                <h4>Additional Details</h4>
                                                <ul class="rfp-kv-list">
                                                    <li v-for="detail in rfpDetails.commonDetails"
                                                        :key="detail.label">
                                                        <span class="kv-label">{{ detail.label }}</span>
                                                        <span class="kv-value">{{ detail.value }}</span>
                                                    </li>
                                                </ul>
                                            </section>
                                        </div>
                                        <aside class="overview-meta">
                                            <h4>At a Glance</h4>
                                            <dl class="meta-list">
                                                <div v-if="rfpDetails.publishedAt">
                                                    <dt>Published</dt>
                                                    <dd>{{ formatDate(rfpDetails.publishedAt) }}</dd>
                                                </div>
                                                <div v-if="rfpDetails.endDate">
                                                    <dt>Response Due</dt>
                                                    <dd>{{ formatDate(rfpDetails.endDate) }}</dd>
                                                </div>
                                                <div v-if="rfpDetails.companyName">
                                                    <dt>Company</dt>
                                                    <dd>{{ rfpDetails.companyName }}</dd>
                                                </div>
                                                <div v-if="rfpDetails.companyWebsiteUrl">
                                                    <dt>Company Website</dt>
                                                    <dd>
                                                        <a :href="buildExternalUrl(rfpDetails.companyWebsiteUrl)"
                                                           target="_blank"
                                                           rel="noopener">
                                                            {{ rfpDetails.companyWebsiteUrl }}
                                                        </a>
                                                    </dd>
                                                </div>
                                                <div v-if="rfpDetails.buyer?.name">
                                                    <dt>Primary Contact</dt>
                                                    <dd>{{ rfpDetails.buyer.name }}</dd>
                                                </div>
                                                <div v-if="rfpDetails.buyer?.email">
                                                    <dt>Email</dt>
                                                    <dd>
                                                        <a :href="`mailto:${rfpDetails.buyer.email}`">
                                                            {{ rfpDetails.buyer.email }}
                                                        </a>
                                                    </dd>
                                                </div>
                                                <div v-if="rfpDetails.phoneNumber">
                                                    <dt>Phone</dt>
                                                    <dd>{{ rfpDetails.phoneNumber }}</dd>
                                                </div>
                                            </dl>
                                        </aside>
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel header="Client's Goals and Needs">
                                <div class="tab-content needs-tab">
                                    <div v-for="section in rfpDetails.goals.sections" :key="section.id"
                                        class="needs-section">
                                        <h3 v-if="section.label" class="needs-section-heading">{{ section.label }}</h3>
                                        <p v-if="section.label && section.explanation" class="needs-section-explanation">
                                            {{ section.explanation }}
                                        </p>
                                        <div class="needs-question-card" v-for="question in section.questions"
                                            :key="question.id || question.question">
                                            <h4>Question: {{ question.question || 'Question' }}</h4>
                                            <p v-if="question.explanation" class="needs-explanation">
                                               Explanation: {{ question.explanation || 'No explanation provided.' }}
                                            </p>
                                            <p class="needs-answer">
                                                Answer: {{ question.answer ? question.answer : 'No answer provided.' }}
                                            </p>
                                        </div>
                                    </div>
                                    <p v-if="!rfpDetails.goals.sections?.length" class="needs-empty">
                                        No discovery responses were provided.
                                    </p>
                                </div>
                            </TabPanel>
                            <TabPanel header="Questionnaire">
                                <div class="tab-content questionnaire-tab">
                                    <Accordion v-if="rfpDetails.questionnaire?.length" :multiple="true">
                                        <AccordionTab v-for="group in rfpDetails.questionnaire" :key="group.group"
                                            :header="group.group">
                                            <div class="questionnaire-group">
                                                <div class="qa-card" v-for="question in group.questions"
                                                    :key="question.id">
                                                    <h4>{{ question.title }}</h4>
                                                    <p v-if="question.description" class="qa-description">
                                                        {{ question.description }}
                                                    </p>
                                                    <div class="qa-answer" v-if="question.answer">
                                                        <template v-if="question.answer.type === 'list'">
                                                            <ul class="qa-answer-list">
                                                                <li v-for="(item, index) in question.answer.value"
                                                                    :key="index">
                                                                    {{ item }}
                                                                </li>
                                                            </ul>
                                                        </template>
                                                        <template v-else-if="question.answer.type === 'html'">
                                                            <span v-html="question.answer.value"></span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ question.answer.value }}</span>
                                                        </template>
                                                    </div>
                                                    <ul v-if="question.documents?.length" class="qa-documents">
                                                        <li v-for="doc in question.documents" :key="doc.id || doc.name">
                                                            <a :href="buildDocumentLink(doc)" target="_blank"
                                                                rel="noopener">
                                                                {{ doc.name }}
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </AccordionTab>
                                    </Accordion>
                                    <p v-else class="needs-empty">
                                        No questionnaire responses were provided.
                                    </p>
                                </div>
                            </TabPanel>
                            <TabPanel header="Pricing Table">
                                <div class="tab-content pricing-tab">
                                    <div v-if="!rfpDetails.pricing?.tables?.length" class="empty-pricing">
                                        No pricing information was submitted for this RFP.
                                    </div>
                                    <div v-for="table in rfpDetails.pricing.tables" :key="table.name"
                                        class="pricing-table-block">
                                        <h4 class="pricing-table-title">{{ table.name }}</h4>
                                        <div class="table-scroll">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Field</th>
                                                        <th v-for="column in table.columns" :key="column.id">
                                                            {{ column.label }}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in table.rows" :key="row.key">
                                                        <td class="field-label">{{ row.label }}</td>
                                                        <td v-for="(value, index) in row.values"
                                                            :key="`${row.key}-${table.columns[index]?.id}`">
                                                            {{ value }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <p v-if="rfpDetails.pricing.notes" class="pricing-note">{{ rfpDetails.pricing.notes
                                        }}</p>
                                </div>
                            </TabPanel>
                        </TabView>
                    </div>

                    <div class="footer-note">
                        <i class="pi pi-info-circle" aria-hidden="true"></i>
                        <span>
                            This response was submitted in the legacy system.
                        </span>
                    </div>
                </template>

                <div v-else class="empty-state">
                    <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                    <div>
                        <h3>Legacy RFP data not found</h3>
                        <p>
                            We could not locate the requested historical RFP. Please verify the link or reach out to
                            support if you believe this is an error.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LegacyRfpService from '../service/LegacyRfpService';

export default {
    name: 'LegacyRfpDetails',
    data() {
        return {
            loading: false,
            rfpId: null,
            vendorId: null,
            rfpDetails: null,
            activeTabIndex: 0,
            apiError: null,
            legacyRfpService: new LegacyRfpService()
        };
    },
    watch: {
        $route: {
            handler() {
                this.initializePage();
            }
        }
    },
    created() {
        this.initializePage();
    },
    methods: {
        async initializePage() {
            this.loading = true;
            this.rfpDetails = null;
            this.activeTabIndex = 0;
            this.apiError = null;

            try {
                const candidateId = this.$route.params.rfpId || this.$route.query.id;
                this.rfpId = candidateId || 'default';
                const vendorCandidate = this.$route.query.vendorId || '';
                this.vendorId = vendorCandidate ? decodeURIComponent(vendorCandidate) : null;
                const response = await this.fetchHistoricRfp(this.rfpId);
                this.rfpDetails = response;
            } finally {
                this.loading = false;
            }
        },
        async fetchHistoricRfp(rfpId) {
            if (!this.vendorId) {
                this.apiError = 'Vendor identifier missing. Unable to load legacy response.';
                return null;
            }

            if (!rfpId || rfpId === 'default') {
                this.apiError = 'RFP identifier missing. Unable to load legacy response.';
                return null;
            }

            try {
                const response = await this.legacyRfpService.getLegacyRfpDetails({
                    vendorId: this.vendorId,
                    rfpId
                });

                if (response?.status === 200 && response.data) {
                    const mapped = this.mapApiResponse(response.data);
                    this.apiError = null;
                    return mapped;
                }

                this.apiError = this.formatApiError(response);
            } catch (error) {
                console.error('Error fetching legacy RFP details:', error);
                this.apiError = 'We could not retrieve the legacy RFP details from the server. Please try again later.';
            }

            return null;
        },
        mapApiResponse(data) {
            if (!data || typeof data !== 'object') {
                return null;
            }

            const mapped = {
                name: data.prokuria_rfp_name || data.rfp_name || data.name,
                status: data.status_display_name || data.status,
                responseStatus: data.vendor_status_display_name || data.vendor_status,
                submittedOn: data.vendor_response_submitted_at || data.submitted_at || data.submittedOn,
                dueDate: data.end_date || data.due_date,
                lastUpdated: data.updated_at || data.last_modified || data.modified_at,
                publishedAt: data.published_at || data.created_at,
                endDate: data.end_date || data.due_date,
                buyer: {
                    company: data.company_name || data.company || data.buyer_company_name || data?.buyer?.company,
                    name: [data.first_name, data.last_name].filter(Boolean).join(' ') || data.contact_name || data?.buyer?.name,
                    email: data.email || data.contact_email || data.buyer_contact_email || data?.buyer?.email
                },
                phoneNumber: data.phone_number,
                companyName: data.company_name || data.buyer_company_name,
                companyWebsiteUrl: data.company_website_url || data.company_url,
                prokuriaRequestId: data.prokuria_request_id || data.prokuria_requestid,
                description: data.description || data.rfp_description || '',
                commonDetails: this.extractCommonDetailsFromApi(data),
                goals: this.extractGoalsFromApi(data),
                questionnaire: this.extractQuestionnaireFromApi(data),
                pricing: this.extractPricingFromApi(data),
                attachments: this.extractGeneralAttachments(data.attachments),
                links: this.extractExternalLinks(data.links)
            };

            return mapped;
        },
        extractCommonDetailsFromApi(data) {
            const details = [];
            const pushDetail = (label, value) => {
                if (value || value === 0) {
                    details.push({ label, value });
                }
            };

            pushDetail('Industry', data.industry || data.industry_name);
            pushDetail('Primary Language', data.primary_language || data.language);
            pushDetail('Estimated Volume', data.estimated_volume_display || data.estimated_volume);
            const responseLead = [data.vendor_contact_first_name, data.vendor_contact_last_name]
                .filter(Boolean)
                .join(' ') || data.response_lead || data.responseLead;
            pushDetail('Response Lead', responseLead);
            pushDetail('Commercial Model', data.commercial_model || data.commercial_model_display);
            pushDetail('Contract Term', data.contract_term || data.contractTerm);
            const regions = Array.isArray(data.regions_proposed)
                ? data.regions_proposed.join(', ')
                : data.regions_proposed || (Array.isArray(data.delivery_regions) ? data.delivery_regions.join(', ') : null);
            pushDetail('Regions Proposed', regions);

            return details;
        },
        extractGoalsFromApi(data) {
            const questions = Array.isArray(data.discoveryQuestions)
                ? data.discoveryQuestions
                    .slice()
                    .sort((a, b) => (a.ordering_number ?? a.sortOrder ?? 0) - (b.ordering_number ?? b.sortOrder ?? 0))
                    .map(item => ({
                        id: item.id,
                        question: item.question || '',
                        explanation: item.explanation || '',
                        answer: item.answer || ''
                    }))
                : [];

            // Group questions under [SECTION] markers (mirrors the buyer-side
            // Step3.vue parser in the RFP repo). A section marker is a
            // pseudo-question whose `question` text starts with "[SECTION]";
            // its label is the remainder, and its description rides on
            // `explanation`. Questions before any marker land in a leading
            // unlabeled group rendered as a flat list. Empty groups are
            // dropped so a heading never appears without questions beneath it.
            const groups = [];
            let current = null;
            for (const q of questions) {
                if (typeof q.question === 'string' && q.question.trim().startsWith('[SECTION]')) {
                    current = {
                        id: q.id || `section-${groups.length}`,
                        label: q.question.replace('[SECTION]', '').trim(),
                        explanation: q.explanation || null,
                        questions: [],
                    };
                    groups.push(current);
                } else {
                    if (!current) {
                        current = { id: 'default', label: null, explanation: null, questions: [] };
                        groups.push(current);
                    }
                    current.questions.push(q);
                }
            }
            const sections = groups.filter(g => g.questions.length > 0);

            return { questions, sections };
        },
        extractQuestionnaireFromApi(data) {
            if (!Array.isArray(data.questions) || !data.questions.length) {
                return [];
            }

            const grouped = data.questions.reduce((acc, question) => {
                const group = question.questionGroup || question.groupName || 'General';
                if (!acc[group]) {
                    acc[group] = [];
                }
                acc[group].push(this.normalizeQuestionnaireQuestion(question));
                return acc;
            }, {});

            return Object.entries(grouped).map(([group, questions]) => ({
                group,
                questions: questions.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            }));
        },
        normalizeQuestionnaireQuestion(question) {
            const response = question.response || {};
            const documents = this.extractQuestionDocuments(response.documents, response.answer);
            const answer = this.formatQuestionAnswer(question, response, documents);

            return {
                id: question.id,
                title: question.title || question.question || question.display_name || question.field_name || 'Question',
                description: question.description || '',
                answer,
                documents,
                sortOrder: question.sortOrder ?? question.ordering_number ?? 0
            };
        },
        formatQuestionAnswer(question, response, documents = []) {
            const rawAnswer = response?.answer;
            const normalized = this.normalizeAnswerValue(rawAnswer);
            const typeId = Number(question.idQuestionType || response.questionTypeId);
            const hasDocuments = Array.isArray(documents) && documents.length > 0;

            const ensureText = value => {
                if (value === null || value === undefined || value === '') {
                    return 'No answer provided.';
                }
                return String(value);
            };

            const defaultAnswer = { type: 'text', value: 'No answer provided.' };

            if (Array.isArray(normalized)) {
                const mapped = normalized
                    .map(value => this.mapAnswerOption(value, question.options))
                    .filter(value => value !== null && value !== undefined && value !== '');
                const list = mapped.length ? mapped : (hasDocuments ? ['Response provided in attachments.'] : []);
                if (!list.length) {
                    return defaultAnswer;
                }
                return { type: 'list', value: list };
            }

            if (normalized === null || normalized === undefined || normalized === '') {
                return hasDocuments ? { type: 'text', value: 'Response provided in attachments.' } : defaultAnswer;
            }

            if (typeof normalized === 'number') {
                return { type: 'text', value: normalized.toLocaleString() };
            }

            if (typeof normalized === 'object') {
                const values = Object.values(normalized)
                    .filter(Boolean)
                    .map(value => this.mapAnswerOption(value, question.options));
                const text = values.length ? values.join(', ') : null;
                if (text) {
                    return { type: 'text', value: text };
                }
                return hasDocuments ? { type: 'text', value: 'Response provided in attachments.' } : defaultAnswer;
            }

            if (typeof normalized === 'string') {
                const trimmed = normalized.trim();
                if (!trimmed) {
                    return hasDocuments ? { type: 'text', value: 'Response provided in attachments.' } : defaultAnswer;
                }

                const withoutBraces = this.stripSimpleBraces(trimmed);
                const mappedValue = this.mapAnswerOption(withoutBraces, question.options);
                const valueToUse = mappedValue !== null && mappedValue !== undefined ? mappedValue : withoutBraces;
                const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(valueToUse);

                switch (typeId) {
                    case 3: {
                        const list = this.normalizeAnswerList(valueToUse, question.options);
                        if (!list.length) {
                            return hasDocuments ? { type: 'text', value: 'Response provided in attachments.' } : defaultAnswer;
                        }
                        return { type: 'list', value: list };
                    }
                    case 2: {
                        const text = this.stripSimpleBraces(valueToUse);
                        return { type: 'text', value: ensureText(text) };
                    }
                    case 5: {
                        return hasDocuments ? { type: 'text', value: 'Response provided in attachments.' } : { type: 'text', value: ensureText(valueToUse) };
                    }
                    case 9: {
                        const formatted = this.formatDate(valueToUse);
                        return { type: 'text', value: formatted };
                    }
                    case 8: {
                        const numeric = Number(valueToUse);
                        return Number.isFinite(numeric)
                            ? { type: 'text', value: numeric.toLocaleString() }
                            : { type: 'text', value: ensureText(valueToUse) };
                    }
                    case 4:
                    case 1:
                        return looksLikeHtml
                            ? { type: 'html', value: valueToUse }
                            : { type: 'html', value: valueToUse.replace(/\n/g, '<br />') };
                    default:
                        return looksLikeHtml
                            ? { type: 'html', value: valueToUse }
                            : { type: 'text', value: ensureText(valueToUse) };
                }
            }

            return { type: 'text', value: ensureText(normalized) };
        },
        normalizeAnswerList(value, options = []) {
            if (Array.isArray(value)) {
                return value
                    .map(item => this.mapAnswerOption(item, options))
                    .filter(item => item !== null && item !== undefined && item !== '');
            }

            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) {
                        return parsed
                            .map(item => this.mapAnswerOption(item, options))
                            .filter(item => item !== null && item !== undefined && item !== '');
                    }
                } catch (error) {
                    // fall through
                }
                return value
                    .split(/\r?\n|,\s*/)
                    .map(item => this.mapAnswerOption(item, options))
                    .filter(item => item !== null && item !== undefined && item !== '');
            }

            return [];
        },
        normalizeAnswerValue(answer) {
            if (answer === null || answer === undefined) {
                return '';
            }

            if (typeof answer === 'string') {
                const trimmed = answer.trim();
                if (!trimmed) {
                    return '';
                }

                const parsed = this.tryParseJson(trimmed);
                if (parsed !== null) {
                    return parsed;
                }

                return trimmed;
            }

            return answer;
        },
        mapAnswerOption(value, options = []) {
            if (value === null || value === undefined || value === '') {
                return '';
            }

            const stringValue = String(value).trim();
            if (!options?.length) {
                return stringValue;
            }

            const byValue = options.find(option => String(option.optionValue).trim() === stringValue);
            if (byValue) {
                return byValue.optionValue;
            }

            const byId = options.find(option => String(option.id).trim() === stringValue);
            if (byId) {
                return byId.optionValue;
            }

            return stringValue;
        },
        stripSimpleBraces(value) {
            if (/^\{[^:]*\}$/.test(value)) {
                return value.replace(/^\{|\}$/g, '').replace(/"/g, '').trim();
            }
            return value;
        },
        extractQuestionDocuments(documents, answer) {
            const parseComplexDocument = (raw) => {
                if (!raw || raw === '{}' || raw === '[]' || raw === '""') {
                    return null;
                }

                let currentValue = raw;

                // Try multiple parsing attempts for deeply nested JSON
                for (let depth = 0; depth < 5 && typeof currentValue === 'string'; depth++) {
                    const trimmed = currentValue.trim();

                    // Remove surrounding quotes if present
                    let cleanValue = trimmed;
                    if ((cleanValue.startsWith('"') && cleanValue.endsWith('"')) ||
                        (cleanValue.startsWith('{') && cleanValue.endsWith('}'))) {

                        // Handle the triple-encoded case like: "{\"{\\\"IdResponseDocument\\\":...}\"}"
                        try {
                            // First parse to remove outer quotes and unescape
                            const firstParse = JSON.parse(cleanValue);
                            if (typeof firstParse === 'string') {
                                // It's still a string, parse again
                                const secondParse = JSON.parse(firstParse);
                                currentValue = secondParse;
                            } else {
                                currentValue = firstParse;
                            }
                        } catch (error) {
                            // If parsing fails, try to extract JSON object directly
                            const jsonMatch = cleanValue.match(/\{.*\}/);
                            if (jsonMatch) {
                                try {
                                    currentValue = JSON.parse(jsonMatch[0].replace(/\\+"/g, '"'));
                                } catch (e) {
                                    // If still failing, try manual extraction
                                    const idMatch = cleanValue.match(/IdResponseDocument[\\"]*:[\\"]*([^"\\}]+)/);
                                    const nameMatch = cleanValue.match(/FileName[\\"]*:[\\"]*([^"\\}]+)/);
                                    if (idMatch && nameMatch) {
                                        currentValue = {
                                            IdResponseDocument: idMatch[1],
                                            FileName: nameMatch[1]
                                        };
                                        break;
                                    }

                                }
                            }
                            break;
                        }
                    } else {
                        break;
                    }
                }

                // Normalize the parsed result
                if (Array.isArray(currentValue)) {
                    return currentValue
                        .map(item => this.normalizeDocument(item))
                        .filter(Boolean);
                }

                if (currentValue && typeof currentValue === 'object') {
                    const normalized = this.normalizeDocument(currentValue);
                    return normalized ? [normalized] : null;
                }

                return null;
            };

            // First try to parse the documents field
            let parsedDocuments = parseComplexDocument(documents);

            // If no documents found in documents field, check the answer field for type 5 questions
            if (!parsedDocuments && typeof answer === 'string') {
                parsedDocuments = parseComplexDocument(answer);
            }

            return parsedDocuments || [];
        },
        normalizeDocument(doc) {
            if (!doc || typeof doc !== 'object') {
                return null;
            }

            const name = doc.FileName || doc.fileName || doc.name || doc.documentName;
            const id = doc.IdResponseDocument || doc.id || doc.documentId || doc.DocumentId;
            const url = doc.url || doc.download_url || doc.downloadUrl;

            if (!name) {
                return null;
            }

            return { name, id, url };
        },
        buildDocumentLink(doc) {
            if (doc?.url) {
                return doc.url;
            }

            const id = doc?.id || doc?.IdResponseDocument;
            if (!id) {
                return '#';
            }
            return `https://arenacxclientapi.prokuria.com/arenacx/DownloadResponseQuestionDocument/${encodeURIComponent(id)}`;
        },
        tryParseJson(value) {
            try {
                return JSON.parse(value);
            } catch (error) {
                return null;
            }
        },
        extractGeneralAttachments(attachments) {
            if (!Array.isArray(attachments)) {
                return [];
            }

            return attachments
                .map(item => {
                    const normalized = this.normalizeDocument({
                        FileName: item.file_name || item.FileName || item.name,
                        IdResponseDocument: item.IdResponseDocument || item.id || item.documentId || item.attachmentId,
                        url: item.download_url || item.downloadUrl || item.url
                    });
                    return normalized;
                })
                .filter(Boolean);
        },
        extractExternalLinks(links) {
            if (!Array.isArray(links)) {
                return [];
            }

            return links
                .map(link => {
                    const url = link.url || link.href;
                    if (!url) {
                        return null;
                    }
                    return {
                        label: link.label || link.title || link.name || url,
                        url
                    };
                })
                .filter(Boolean);
        },
        buildExternalUrl(url) {
            if (!url) {
                return '#';
            }
            if (/^https?:\/\//i.test(url)) {
                return url;
            }
            return `https://${url}`;
        },
        formatRichText(value) {
            if (!value) {
                return '';
            }
            if (/<\/?[a-z][\s\S]*>/i.test(value)) {
                return value;
            }
            return value.replace(/(?:\r\n|\r|\n)/g, '<br />');
        },
        extractPricingFromApi(data) {
            const tables = [];

            const processTable = (table, contextLabel) => {
                if (!table) {
                    return;
                }

                const tableName = table.table_name || table.name || contextLabel || 'Pricing';
                const entries = [];

                if (Array.isArray(table.table_data)) {
                    table.table_data.forEach(entry => {
                        if (entry && typeof entry === 'object' && Object.keys(entry).length) {
                            entries.push({ ...entry });
                        }
                    });
                } else {
                    const rows = table.table_data?.rows || table.rows || [];
                    rows.forEach(row => {
                        if (row && typeof row === 'object' && Object.keys(row).length) {
                            entries.push({ ...row });
                        }
                    });
                }

                if (entries.length) {
                    const pricingTable = this.createPricingTable(tableName, entries);
                    if (pricingTable) {
                        tables.push(pricingTable);
                    }
                }

                if (Array.isArray(table.nestedTables)) {
                    table.nestedTables.forEach(nested => processTable(nested, tableName));
                }
            };

            if (Array.isArray(data.pricing_data)) {
                data.pricing_data.forEach(table => processTable(table, 'Pricing'));
            }

            return {
                tables,
                notes: data.pricing_notes || data.pricing_commentary || ''
            };
        },
        createPricingTable(tableName, entries) {
            const normalizedEntries = entries.map((entry, index) => this.normalizePricingEntry(entry, index));
            if (!normalizedEntries.length) {
                return null;
            }

            let hasDescription = false;
            let hasServiceType = false;
            let hasUnit = false;
            let hasPricingRemarks = false;
            let hasBaseRate = false;

            const locationLetters = new Set();
            const additionalKeyMap = new Map();

            normalizedEntries.forEach(item => {
                if (item.description) {
                    hasDescription = true;
                }
                if (item.serviceType) {
                    hasServiceType = true;
                }
                if (item.unitOfMeasure) {
                    hasUnit = true;
                }
                if (item.pricingRemarks) {
                    hasPricingRemarks = true;
                }
                if (item.baseRate.present) {
                    hasBaseRate = true;
                }
                Object.keys(item.locations).forEach(letter => locationLetters.add(letter));
                Object.keys(item.additional).forEach(key => {
                    if (!additionalKeyMap.has(key)) {
                        additionalKeyMap.set(key, this.formatHeaderLabel(key));
                    }
                });
            });

            const sortedLocationLetters = Array.from(locationLetters).sort();

            const columns = [];
            const addColumn = (id, label, getter) => {
                columns.push({ id, label, getValue: getter });
            };

            addColumn('service', 'Service', entry => this.formatTextValue(entry.service));

            if (hasBaseRate) {
                addColumn('baseRate', 'Rate (USD)', entry => this.formatRateDisplay(entry.baseRate));
            }

            if (hasDescription) {
                addColumn('description', 'Description', entry => this.formatTextValue(entry.description));
            }

            if (hasServiceType) {
                addColumn('serviceType', 'Service Type', entry => this.formatTextValue(entry.serviceType));
            }

            if (hasUnit) {
                addColumn('unitOfMeasure', 'Unit Of Measure', entry => this.formatTextValue(entry.unitOfMeasure));
            }

            if (hasPricingRemarks) {
                addColumn('pricingRemarks', 'Pricing Remarks', entry => this.formatTextValue(entry.pricingRemarks));
            }

            additionalKeyMap.forEach((label, key) => {
                addColumn(`additional-${key}`, label, entry => this.formatPricingCellValue(key, entry.additional[key]));
            });

            sortedLocationLetters.forEach(letter => {
                addColumn(
                    `location-${letter}`,
                    `Location ${letter}`,
                    entry => this.formatLocationDisplay(entry.locations[letter])
                );
                addColumn(
                    `location-${letter}-rate`,
                    'Rate (USD)',
                    entry => this.formatLocationRate(entry.locations[letter])
                );
            });

            const rows = normalizedEntries.map((entry, index) => ({
                key: entry.key,
                label: entry.service || `Item ${index + 1}`,
                values: columns.map(column => column.getValue(entry))
            }));

            return {
                name: tableName || 'Pricing',
                columns: columns.map(column => ({
                    id: column.id,
                    label: column.label
                })),
                rows
            };
        },
        normalizePricingEntry(entry, index) {
            const usedKeys = new Set();
            const extractField = candidateKeys => {
                for (const key of candidateKeys) {
                    if (Object.prototype.hasOwnProperty.call(entry, key)) {
                        const value = entry[key];
                        if (this.isMeaningfulValue(value)) {
                            usedKeys.add(key);
                            return value;
                        }
                    }
                }
                return undefined;
            };

            const service = extractField(['Service', 'service', 'Item', 'item', 'Line Item', 'line item', 'Name', 'name']);
            const description = extractField(['Description', 'description', 'Summary', 'summary']);
            const serviceType = extractField(['Service Type', 'service type', 'ServiceType', 'Role Type', 'role type']);
            const unitOfMeasure = extractField(['Unit Of Measure', 'unit of measure', 'Unit', 'unit']);
            const pricingRemarks = extractField(['Pricing Remarks', 'pricing remarks', 'Remarks', 'remarks']);
            const baseRateRaw = extractField(['Rate (USD)', 'Rate', 'USD Rate', 'USD Amount', 'Amount', 'Cost']);

            const locations = {};
            Object.entries(entry).forEach(([key, value]) => {
                const match = key.match(/^Location\s+([A-Z])\s*:\s*(.+)$/i);
                if (!match) {
                    return;
                }
                usedKeys.add(key);
                const letter = match[1].toUpperCase();
                const field = match[2].trim().toLowerCase();
                if (!locations[letter]) {
                    locations[letter] = {};
                }
                if (field === 'name') {
                    locations[letter].name = value;
                } else if (field === 'type') {
                    locations[letter].type = value;
                } else if (field.includes('rate') || field.includes('amount') || field.includes('cost')) {
                    locations[letter].rate = value;
                }
            });

            const additional = {};
            Object.entries(entry).forEach(([key, value]) => {
                if (usedKeys.has(key)) {
                    return;
                }
                if (!this.isMeaningfulValue(value)) {
                    return;
                }
                additional[key] = value;
            });

            return {
                key: `row-${index}`,
                service: this.cleanTextValue(service) || `Entry ${index + 1}`,
                description: this.cleanTextValue(description),
                serviceType: this.cleanTextValue(serviceType),
                unitOfMeasure: this.cleanTextValue(unitOfMeasure),
                pricingRemarks: this.cleanTextValue(pricingRemarks),
                baseRate: {
                    raw: baseRateRaw,
                    numeric: this.parseNumericValue(baseRateRaw),
                    present: this.isMeaningfulValue(baseRateRaw)
                },
                locations,
                additional
            };
        },
        formatHeaderLabel(key) {
            if (!key) {
                return '';
            }
            return key
                .replace(/_/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .replace(/\b\w/g, char => char.toUpperCase());
        },
        formatTextValue(value) {
            const cleaned = this.cleanTextValue(value);
            return cleaned !== null ? cleaned : '—';
        },
        formatRateDisplay(baseRate) {
            if (!baseRate || !baseRate.present) {
                return '—';
            }
            if (baseRate.numeric !== null) {
                return this.formatNumericDisplay(baseRate.numeric);
            }
            return this.formatTextValue(baseRate.raw);
        },
        formatLocationDisplay(location) {
            if (!location) {
                return '—';
            }
            const name = this.cleanTextValue(location.name);
            const type = this.cleanTextValue(location.type);
            if (!name && !type) {
                return '—';
            }
            const namePart = name || '--';
            const typePart = type || '--';
            return `${namePart} (${typePart})`;
        },
        formatLocationRate(location) {
            if (!location) {
                return '—';
            }
            const numeric = this.parseNumericValue(location.rate);
            if (numeric !== null) {
                return this.formatNumericDisplay(numeric);
            }
            return this.formatTextValue(location.rate);
        },
        formatPricingCellValue(key, value) {
            if (value === null || value === undefined || value === '') {
                return '—';
            }

            if (Array.isArray(value)) {
                return value
                    .map(item => this.formatPricingCellValue(key, item))
                    .filter(item => item !== '—')
                    .join(', ') || '—';
            }

            if (typeof value === 'object') {
                const flattened = Object.entries(value)
                    .map(([nestedKey, nestedValue]) => `${this.formatHeaderLabel(nestedKey)}: ${this.formatPricingCellValue(nestedKey, nestedValue)}`)
                    .join(', ');
                return flattened || '—';
            }

            const numeric = this.parseNumericValue(value);
            if (numeric !== null) {
                return this.formatNumericDisplay(numeric);
            }

            return this.formatTextValue(value);
        },
        formatNumericDisplay(value) {
            if (value === null || value === undefined || Number.isNaN(value)) {
                return '—';
            }
            return Number(value).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4
            });
        },
        cleanTextValue(value) {
            if (value === null || value === undefined) {
                return null;
            }
            if (typeof value === 'number' && Number.isFinite(value)) {
                return Number(value).toLocaleString();
            }
            if (typeof value === 'string') {
                const trimmed = value.trim();
                return trimmed.length ? trimmed : null;
            }
            return String(value);
        },
        isMeaningfulValue(value) {
            if (value === null || value === undefined) {
                return false;
            }
            if (typeof value === 'string') {
                return value.trim().length > 0;
            }
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            if (typeof value === 'object') {
                return Object.keys(value).length > 0;
            }
            return true;
        },
        parseNumericValue(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            if (typeof value === 'number' && Number.isFinite(value)) {
                return value;
            }

            if (typeof value === 'string') {
                const normalized = value.replace(/[^0-9.+-]/g, '');
                if (!normalized || normalized === '-' || normalized === '+' || normalized === '.' || normalized === '+.' || normalized === '-.') {
                    return null;
                }
                const parsed = Number(normalized);
                if (Number.isFinite(parsed)) {
                    return parsed;
                }
            }

            return null;
        },
        formatDate(value) {
            if (!value) {
                return '—';
            }
            const date = new Date(value);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        formatCurrency(value) {
            if (value === null || value === undefined || Number.isNaN(value)) {
                return '—';
            }

            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '—';
            }

            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: 'USD'
            }).format(numericValue);
        },
        formatApiError(response) {
            if (!response) {
                return 'Unexpected response from the server. Showing sample data while we investigate.';
            }

            if (response.status === 404) {
                return 'Legacy response not found in the archive. Showing sample data instead.';
            }

            if (response.status >= 500) {
                return 'The legacy archive is temporarily unavailable. Showing sample data until the service is restored.';
            }

            return response.data?.message
                || `Unexpected status (${response.status}) returned by the legacy archive. Showing sample data.`;
        }
    }
};
</script>

<style scoped lang="scss">
.legacy-rfp-details {
    .card {
        min-height: 480px;
    }
}

.loading-state,
.empty-state {
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-content: center;
    min-height: 220px;
    text-align: center;

    i {
        font-size: 1.5rem;
        color: #00796b;
    }
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;

    h2 {
        margin: 0;
        font-size: 1.8rem;
    }

    .subtitle {
        margin: 0.25rem 0 0;
        color: #606060;
    }
}

.status-block {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.35rem;
}

.status-pill {
    background-color: #e4edd9;
    color: #2e7d32;
    border-radius: 999px;
    padding: 0.25rem 0.75rem;
    font-weight: 600;
}

.legacy-pill {
    background-color: #f4f4f5;
    color: #555;
    border-radius: 999px;
    padding: 0.25rem 0.75rem;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.summary-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-top: 1.5rem;
}

.summary-card {
    display: flex;
    flex-direction: column;
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 1rem;
    border: 1px solid #e1e5eb;

    .label {
        font-size: 0.85rem;
        color: #707070;
    }

    .value {
        font-weight: 600;
        margin-top: 0.3rem;
    }
}

.api-error-banner {
    margin-top: 1rem;
    display: flex;
    gap: 0.75rem;
    align-items: center;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    background-color: #fff4e5;
    color: #8a4b16;
    border: 1px solid #f5c27a;

    i {
        font-size: 1.25rem;
    }
}

.card-section {
    margin-top: 1.5rem;
}

.tab-content {
    padding: 1.5rem 0.5rem;
}

.overview-tab {
    padding-top: 0;
}

.overview-grid {
    display: grid;
    gap: 2rem;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    align-items: start;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
}

.overview-section {
    margin-bottom: 2rem;

    h4 {
        margin: 2rem 0 0.75rem;
    }
}

.overview-main {
    display: flex;
    flex-direction: column;
}

.overview-meta {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1.5rem;

    h4 {
        margin: 0 0 1rem;
    }
}

.meta-list {
    display: grid;
    gap: 0.75rem;

    div {
        display: grid;
        gap: 0.25rem;
    }

    dt {
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #64748b;
    }

    dd {
        margin: 0;
        font-weight: 600;
        color: #1f2937;
        word-break: break-word;
    }

    a {
        color: #00796b;
        text-decoration: underline;

        &:hover {
            color: #004d40;
        }
    }
}

.rfp-description {
    line-height: 1.75;
    color: #1f2937;
}

.rfp-doc-list,
.rfp-link-list,
.rfp-kv-list {
    margin: 0;
    padding-left: 1.2rem;
    color: #1f2937;

    li {
        margin-bottom: 0.5rem;
    }

    a {
        color: #00796b;
        text-decoration: underline;

        &:hover {
            color: #004d40;
        }
    }
}

.rfp-kv-list {
    list-style: none;
    padding: 0;

    li {
        display: flex;
        flex-direction: column;
        padding: 0.75rem 0;
        border-bottom: 1px solid #e2e8f0;

        &:last-child {
            border-bottom: none;
        }
    }
}

.kv-label {
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
}

.kv-value {
    font-weight: 600;
    color: #1f2937;
    margin-top: 0.25rem;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
}

.details-item {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border: 1px solid #e1e5eb;
    border-radius: 6px;
    padding: 1rem;

    .detail-label {
        font-size: 0.85rem;
        color: #606060;
    }

    .detail-value {
        margin-top: 0.4rem;
        font-weight: 600;
        color: #1f2933;
    }
}

.needs-question-card {
    border: 1px solid #e1e5eb;
    border-radius: 6px;
    padding: 1rem;
    background-color: #ffffff;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);

    &+.needs-question-card {
        margin-top: 1rem;
    }

    h4 {
        margin: 0 0 0.5rem;
    }
}

.needs-answer {
    margin: 0;
    color: #1f2933;
    font-weight: 500;
}

.needs-empty {
    margin: 0;
    color: #6b7280;
}

.needs-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    & + & {
        margin-top: 1.5rem;
    }
}

.needs-section-heading {
    margin: 0;
    font-size: 1.05rem;
    color: #1f2933;
    border-bottom: 1px solid #e1e5eb;
    padding-bottom: 0.35rem;
}

.needs-section-explanation {
    margin: -0.5rem 0 0;
    color: #475467;
    font-style: italic;
}

.questionnaire-tab {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.qa-card {
    background-color: #ffffff;
    border: 1px solid #e1e5eb;
    border-radius: 6px;
    padding: 1.25rem;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);

    h4 {
        margin-top: 0;
        margin-bottom: 0.5rem;
    }
}

.questionnaire-group {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem 0;
}

.qa-description {
    margin: 0 0 0.5rem;
    color: #475467;
}

.qa-answer {
    margin: 0;
    color: #1f2933;
    line-height: 1.6;
}

.qa-answer-list {
    margin: 0;
    padding-left: 1.25rem;

    li {
        list-style-type: disc;
    }
}

.qa-documents {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
    color: #1f2933;

    a {
        color: #00796b;
        text-decoration: underline;

        &:hover {
            color: #004d40;
        }
    }
}

.pricing-tab {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .pricing-table-block {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .pricing-table-title {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }

    .table-scroll {
        overflow-x: auto;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        background-color: #ffffff;
    }

    table {
        width: 100%;
        min-width: 640px;
        border-collapse: collapse;
    }

    th,
    td {
        padding: 0.75rem;
        border: 1px solid #dee2e6;
        text-align: left;
        vertical-align: top;
    }

    th {
        background-color: #f1f5f9;
        font-weight: 600;
    }

    .field-label {
        font-weight: 600;
        background-color: #f8fafc;
        white-space: nowrap;
        min-width: 180px;
    }

    .pricing-note {
        margin-top: 0.5rem;
        font-size: 0.9rem;
        color: #475467;
    }
}

.empty-pricing {
    padding: 1rem;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    background-color: #f8fafc;
    color: #475467;
    text-align: center;
}

.footer-note {
    margin-top: 2rem;
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 1rem;
    border-radius: 6px;
    background-color: #eef7f4;
    color: #2f4f4f;

    i {
        margin-top: 0.2rem;
    }

    a {
        color: #00796b;
        text-decoration: underline;
    }
}

.section-title {
    margin-top: 2rem;
}

</style>
